import { useTranslation } from 'react-i18next';
import { useRequiredParams } from 'app/router/params';
import { useEffect, useState } from 'react';
import request from 'shared/lib/request';
import Calendar from 'react-widgets/Calendar';

import { useSimpleVersions, SectionEntry } from 'entities/document';
import { api } from 'shared/api/client';

interface Version {
  documentRevisionId: string;
  name: string;
  updatedAt: string;
}

interface DocumentRevision {
  revisionId: string;
  content?: { sections?: SectionEntry[] };
}

interface DocChange {
  revisions: { revisionId: string }[];
}

const formatDate = (date: string | number | Date) => {
  const options = { year: 'numeric', month: 'numeric', day: 'numeric' } as const;
  return new Date(date).toLocaleDateString('sv-SV', options);
};

interface VersionListItemProps {
  version: Version;
  onClick: (revisionId: string) => void;
  selectedRevisionId: string;
  enabled: boolean;
}

const VersionListItem = ({ version, onClick, selectedRevisionId, enabled }: VersionListItemProps) => {
  const { t } = useTranslation('revision');

  const isSelected = version.documentRevisionId === selectedRevisionId;
  const handleOnClick = () => {
    onClick(version.documentRevisionId);
  };

  if (!enabled) {
    return (
      <li>
        <span className={`dropdown-item cursor-pointer d-flex disabled`}>
          {version.name} ({t('selector.not-in-named-version')})
        </span>
      </li>
    );
  }

  return (
    <li>
      <span className={`dropdown-item cursor-pointer d-flex  ${isSelected ? 'text-info justify-content-between' : ''}`} onClick={handleOnClick}>
        {version.name}
        {isSelected ? <i className="fa-regular fa-check" /> : null}
      </span>
    </li>
  );
};

interface VersionToggleButtonProps {
  numVersions: number;
  open: boolean;
  toggle: (open: boolean) => void;
}

const VersionToggleButton = ({ numVersions, open, toggle }: VersionToggleButtonProps) => {
  const { t } = useTranslation('revision');

  if (numVersions < 4) {
    return null;
  }

  const handleClick = () => {
    toggle(!open);
  };

  return (
    <li>
      <span className="dropdown-item cursor-pointer text-info" onClick={handleClick}>
        {open ? (
          <>
            <span className="me-1">{t('selector.show-less')}</span> <i className="fa-regular fa-angle-up" />
          </>
        ) : (
          <>
            <span className="me-1">{t('selector.show-more')}</span> <i className="fa-regular fa-angle-down" />
          </>
        )}
      </span>
    </li>
  );
};

interface VersionListProps {
  versions: Version[];
  onClickVersion: (revisionId: string) => void;
  selectedRevisionId: string;
  versionsWithSection: string[];
}

const VersionList = ({ versions, onClickVersion, selectedRevisionId, versionsWithSection }: VersionListProps) => {
  const [showAll, setShowAll] = useState(false);

  return (
    <ul className="px-1">
      {versions
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
        .map((version, index) => {
          if (!showAll && index > 2) {
            return null;
          }
          return (
            <VersionListItem
              key={version.documentRevisionId}
              enabled={versionsWithSection.includes(version.documentRevisionId)}
              version={version}
              onClick={onClickVersion}
              selectedRevisionId={selectedRevisionId}
            />
          );
        })}
      <VersionToggleButton numVersions={versions.length} open={showAll} toggle={setShowAll} />
    </ul>
  );
};

interface SelectedDateProps {
  versions: Version[];
  selectedDate: Date | null;
  selectedRevisionId: string;
}

const SelectedDate = ({ versions, selectedDate, selectedRevisionId }: SelectedDateProps) => {
  const selectedNamedVersion = versions.find((version) => version.documentRevisionId === selectedRevisionId);

  if (selectedNamedVersion || !selectedDate) {
    return null;
  }

  return (
    <div className="d-flex justify-content-between align-items-center mb-2 ">
      <span className="bg-gray-200 p-2 rounded-1">{formatDate(selectedDate)}</span> <i className="fa-regular fa-check text-info" />
    </div>
  );
};

interface CalendarSectionProps {
  versions: Version[];
  documentId: string | number;
  onChangeVersion: (revisionId: string) => void;
  selectedRevisionId: string;
}

const CalendarSection = ({ versions, documentId, onChangeVersion, selectedRevisionId }: CalendarSectionProps) => {
  const { t } = useTranslation('revision');

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const dateUrl = (toDate: Date | null) => {
    if (!toDate) {
      return '';
    }
    const options = { year: 'numeric', month: 'numeric', day: 'numeric' } as const;
    const fromDateUrl = subtractDays(new Date(toDate), 7).toLocaleDateString('sv-SV', options);
    // time matters and 00:00 wont match any changes that day
    const toDateUrl = addDays(new Date(toDate), 1).toLocaleDateString('sv-SV', options);
    return `?fromDate=${fromDateUrl}&toDate=${toDateUrl}`;
  };

  const addDays = (date: Date, days: number) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };

  const subtractDays = (date: Date, days: number) => {
    const result = new Date(date);
    result.setDate(result.getDate() - days);
    return result;
  };

  const handleCalendarSelection = (value: Date | null) => {
    setSelectedDate(value);

    request
      .get(`/gaby/documents/${documentId}/changes${dateUrl(value)}`)
      .then((res: { data: DocChange[] }) => res.data)
      .then((docChanges: DocChange[]) => {
        const newRevisionId = docChanges[0].revisions[docChanges[0].revisions.length - 1].revisionId;
        if (newRevisionId !== selectedRevisionId) {
          onChangeVersion(newRevisionId);
        }
      });
  };

  return (
    <div className="px-r-1-25">
      <h4 className="subheading">{t('selector.date')}</h4>
      <SelectedDate versions={versions} selectedDate={selectedDate} selectedRevisionId={selectedRevisionId} />
      <Calendar max={new Date()} min={new Date(2020, 1, 1)} onChange={(value: Date | null) => handleCalendarSelection(value)} value={selectedDate} />
    </div>
  );
};

interface RevisionSelectorProps {
  handleChangeVersion: (revisionId: string) => void;
  documentId: string | number;
  currentDocument: unknown;
  revisionId: string;
}

const RevisionSelector = (props: RevisionSelectorProps): JSX.Element | null => {
  const { t } = useTranslation('revision');

  const { handleChangeVersion, documentId, currentDocument, revisionId } = props;
  const { sectionId } = useRequiredParams('sectionId');
  const { data: versions = [] } = useSimpleVersions(documentId);
  const [versionsWithSection, setVersionsWithSection] = useState<string[]>([]);

  useEffect(() => {
    if (!versions.length) return;
    // Find which versions include the currently viewed section
    Promise.all(
      (versions as Version[]).map((version) =>
        api
          .GET('/documents/{revisionId}', {
            params: { path: { revisionId: version.documentRevisionId }, query: { compileSass: 'true' } },
          })
          .then((res) => res.data)
      )
    ).then((documents: DocumentRevision[]) => {
      const filteredDocs = documents.filter((document) => document.content?.sections?.some((section: SectionEntry) => section.superId === sectionId));
      setVersionsWithSection(filteredDocs.map((doc) => doc.revisionId));
    });
  }, [revisionId, versions, sectionId]);

  if (!currentDocument) {
    return null;
  }

  return (
    <>
      <ul className="px-1">
        <li>
          <span className="dropdown-item cursor-pointer" onClick={() => handleChangeVersion('current-draft')}>
            {t('selector.last-changes')}
          </span>
        </li>
        <li>
          <span className="dropdown-item cursor-pointer" onClick={() => handleChangeVersion('current-draft')}>
            {t('selector.last-login')}
          </span>
        </li>
      </ul>
      <hr className="bg-gray-300" />
      <h4 className="subheading px-r-1-25">{t('selector.named-version')}</h4>
      <VersionList
        versions={versions as Version[]}
        onClickVersion={handleChangeVersion}
        selectedRevisionId={revisionId}
        versionsWithSection={versionsWithSection}
      />
      <hr className="bg-gray-300" />
      <CalendarSection versions={versions as Version[]} documentId={documentId} onChangeVersion={handleChangeVersion} selectedRevisionId={revisionId} />
    </>
  );
};

export default RevisionSelector;
